import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";
import { Button, RichText, TableBody } from "../../models";
import { BlockHeader } from "../../../common/block-header/BlockHeader";

type Props = {
  title: RichText;
  table: TableBody;
  button?: Button[];
  footnote?: string;
  isColorBackground?: boolean;
};

export const Table: FC<Props> = ({
  title,
  table,
  button,
  footnote,
  isColorBackground,
}) => (
  <div
    className={classNames(
      "mt-20 px-5",
      "lg:px-16",
      "xl:w-full xl:px-24",
      { "bg-dark py-20 lg:py-25 xl:py-30": isColorBackground },
      { "lg:mt-25 xl:mt-40": !isColorBackground },
    )}
  >
    <div className={classNames("xl:max-w-container xl:mx-auto")}>
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <BlockHeader
          richText={title}
          className={classNames(
            "text-large text-primary-content",
            "xl:text-xx-large",
          )}
        />
        {table ? (
          <div className={classNames("mt-15")}>
            <table
              className={classNames("w-full", {
                "border-divider border-b": footnote || button?.length,
              })}
            >
              {table.thead?.length && (
                <thead className={classNames("text-primary-content")}>
                  <tr>
                    {table.thead.map((col) => (
                      <th
                        key={col._uid}
                        className={classNames(
                          "py-5 pr-5 align-top font-normal",
                          "text-extra-small text-start",
                          "md:text-regular md:py-6 md:pr-6",
                          "xl:text-medium",
                          "xl:py-7 xl:pr-7",
                        )}
                      >
                        {col.value}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              {table.tbody?.length && (
                <tbody className="text-base-400">
                  {table.tbody?.map((row) => (
                    <tr key={row._uid} className={"border-divider border-t"}>
                      {row.body.map((col) => (
                        <td
                          key={col._uid}
                          className={classNames(
                            "py-5 pr-5 align-top",
                            "text-extra-small",
                            "md:text-regular md:py-6 md:pr-6",
                            "xl:text-medium",
                            "xl:py-7 xl:pr-7",
                          )}
                        >
                          {col.value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        ) : null}
        {button?.length ? (
          <div className="mt-5 lg:mt-7">
            <LinkButtonAdapter data={button[0]} />
          </div>
        ) : null}
        {footnote ? (
          <p
            className={classNames(
              "text-extra-small text-base-400 mt-5",
              "lg:mt-15",
            )}
          >
            {footnote}
          </p>
        ) : null}
      </Fade>
    </div>
  </div>
);
